import { Injectable, computed, signal } from '@angular/core';
import type { Signal, WritableSignal } from '@angular/core';

export interface SectionStateConfig {
  isDirty: Signal<boolean>;
  badgeCount?: Signal<number | null>;
  save?: (entityId: string) => Promise<void> | void;
}

export interface SectionSaveResult {
  sectionId: string;
  success: boolean;
  error?: unknown;
}

@Injectable()
export class EditStateCoordinator {
  private readonly sections = signal<Map<string, SectionStateConfig>>(new Map());

  readonly entityId: WritableSignal<string | null> = signal(null);
  readonly isNew: WritableSignal<boolean> = signal<boolean>(false);
  readonly isEditMode: WritableSignal<boolean> = signal<boolean>(false);
  /** Additional context data sections can use (e.g., employeeId for person-edit planning sections) */
  readonly context: WritableSignal<Record<string, unknown>> = signal({});

  readonly dirtySections = computed(() => {
    const map = this.sections();
    const dirty: string[] = [];
    for (const [id, config] of map.entries()) {
      if (config.isDirty()) {
        dirty.push(id);
      }
    }
    return dirty;
  });

  readonly hasDirtyChanges = computed(() => this.dirtySections().length > 0);

  readonly sectionBadges = computed(() => {
    const map = this.sections();
    const badges: Record<string, number> = {};
    for (const [id, config] of map.entries()) {
      if (config.badgeCount) {
        const count = config.badgeCount();
        if (count !== null && count !== undefined) {
          badges[id] = count;
        }
      }
    }
    return badges;
  });

  registerSection(sectionId: string, config: SectionStateConfig): void {
    const map = new Map(this.sections());
    map.set(sectionId, config);
    this.sections.set(map);
  }

  unregisterSection(sectionId: string): void {
    const map = new Map(this.sections());
    if (map.delete(sectionId)) {
      this.sections.set(map);
    }
  }

  async saveAll(entityId: string): Promise<SectionSaveResult[]> {
    const map = this.sections();
    const promises: Promise<SectionSaveResult>[] = [];

    for (const [sectionId, config] of map.entries()) {
      if (config.isDirty() && config.save) {
        promises.push(
          (async () => {
            try {
              if (config.save) {
                await config.save(entityId);
              }
              return { sectionId, success: true };
            } catch (error) {
              return { sectionId, success: false, error };
            }
          })()
        );
      }
    }

    return Promise.all(promises);
  }
}
